/**
 * Which way to point the boat symbol.
 *
 * HEADING and COURSE OVER GROUND are different things, and a plotter that
 * pretends otherwise misleads. Heading is where the bow points, which needs a
 * compass. COG is the direction we are actually travelling over the ground,
 * which a GPS derives from successive positions. With tide or leeway on, the
 * two differ by several degrees, and that difference is the whole point of a
 * lot of navigation.
 *
 * The defect this fixes (fb-msbve96o-1): the device fix path sets position,
 * COG, SOG and accuracy on every fix and never touches heading, so
 * `ownship.heading` sat at its initial 0 forever. On a phone, which has no
 * compass feeding this, the boat symbol pointed at true north the whole time,
 * whatever the boat was doing. A symbol that confidently shows the wrong way
 * round is worse than one that admits it does not know.
 *
 * So, in order:
 *   1. A real heading, when we have one. Nothing beats a compass.
 *   2. Otherwise COG, but ONLY while genuinely moving. COG is meaningless at
 *      rest: the fix jitters and the derived course swings wildly, which would
 *      spin the boat on the screen while it sat still on a mooring.
 *   3. Otherwise hold whatever we were last pointing at. Stale is better than
 *      spinning, and much better than snapping to north.
 */

/** Below this speed, COG is noise rather than a direction. */
export const COG_STEADY_KN = 0.5;

export interface IconHeadingInput {
  /** True heading in degrees, if a compass or the simulator is supplying one. */
  heading: number | null;
  /** Course over ground in degrees. */
  cog: number;
  /** Speed over ground in knots. */
  sog: number;
  /** What the symbol is pointing at now, so we can hold it when we must. */
  last: number | null;
}

/**
 * The bearing to draw the boat symbol at, or null when we have never had
 * anything to point at (a first fix while stopped). A null caller should draw
 * the symbol unrotated, or better, in a form that does not imply a direction.
 */
export function ownshipIconHeading(input: IconHeadingInput): number | null {
  if (input.heading != null && Number.isFinite(input.heading)) return norm(input.heading);
  if (input.sog >= COG_STEADY_KN && Number.isFinite(input.cog)) return norm(input.cog);
  return input.last != null && Number.isFinite(input.last) ? norm(input.last) : null;
}

/** Degrees into 0..360. */
function norm(deg: number): number {
  return ((deg % 360) + 360) % 360;
}
